import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Fingerprint, HelpCircle, Trash2, UserCog } from 'lucide-react';
import { ApiError } from '../api/client';
import * as authApi from '../api/auth';
import * as passkeyApi from '../api/passkeys';
import { useAuth } from '../auth/AuthContext';
import { UpgradeModal } from '../components/UpgradeModal';
import { Passkey, ROLE_LABELS, ProfileUpdate } from '../types/auth';
import { credentialToJSON, parseCreationOptionsFromJSON } from '../utils/webauthn';

const supportsPasskeys = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

const formatDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export function Profile() {
  const { user, updateProfile } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Profile</h1>
            <p className="text-blue-300">Your login details and account security.</p>
          </div>
          <div className="flex items-center gap-4">
            {user?.role && (
              <button
                onClick={() => setShowUpgrade(true)}
                className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Upgrade/Renew
              </button>
            )}
            <Link
              to="/options"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </Link>
          </div>
        </div>

        <SubscriptionLine />

        {user && <ProfileDetailsForm displayName={user.displayName} email={user.email} onSave={updateProfile} />}

        <SecurityQuestionsSection />

        <PasskeysSection />

        {showUpgrade && user?.role && (
          <UpgradeModal
            currentRole={user.role}
            needsRoleSelection={user.needsRoleSelection}
            roleExpiresAt={user.roleExpiresAt}
            onClose={() => setShowUpgrade(false)}
          />
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- subscription

/**
 * The person's platform-wide access level and its end date, right above "Your
 * profile" — the Upgrade/Renew button that opens {@link UpgradeModal} lives in the
 * page's top banner instead, so this is display-only.
 */
function SubscriptionLine() {
  const { user } = useAuth();

  if (!user?.role) return null;

  const isExpired = user.roleExpiresAt != null && user.roleExpiresAt < Date.now();
  // "Payment pending" (mid-checkout) and "Expired" (lapsed) are both states of
  // needsRoleSelection, distinguished only by whether an expiry date is actually in
  // the past — a role that's never been priced-and-paid has no expiry at all yet.
  const isPending = user.needsRoleSelection && !isExpired;

  return (
    <p className="text-lg text-white mb-6">
      {ROLE_LABELS[user.role]}
      {isPending && (
        <span className="ml-2 text-xs text-amber-300/90">Payment pending</span>
      )}
      {user.roleExpiresAt != null && !isPending && (
        <span className={`ml-2 text-xs ${isExpired ? 'text-red-300' : 'text-blue-300/70'}`}>
          {isExpired ? 'Expired' : 'Expires'} {formatDate(user.roleExpiresAt)}
        </span>
      )}
    </p>
  );
}

// ------------------------------------------------------------- your profile

interface ProfileDetailsFormProps {
  displayName: string;
  email: string;
  onSave: (update: ProfileUpdate) => Promise<void>;
}

function ProfileDetailsForm({ displayName, email, onSave }: ProfileDetailsFormProps) {
  const [name, setName] = useState(displayName);
  const [mail, setMail] = useState(email);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await onSave({
        displayName: name.trim(),
        email: mail.trim(),
        currentPassword,
        newPassword: newPassword.trim() || undefined,
      });
      setCurrentPassword('');
      setNewPassword('');
      setNotice('Profile updated.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not update your profile.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
      <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
        <UserCog className="w-5 h-5 text-blue-300" />
        Your profile
      </h2>
      <p className="text-sm text-blue-300/80 mb-4">
        Changing anything here, including your name, requires your current password.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              Display name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              Email address
            </label>
            <input
              type="email"
              value={mail}
              onChange={e => { setMail(e.target.value); setError(''); }}
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              Current password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setError(''); }}
              placeholder="Required to save"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              New password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setError(''); }}
              placeholder="Leave blank to keep your current password"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={busy || !name.trim() || !mail.trim() || !currentPassword}
          className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
        >
          Save Changes
        </button>

        {error && <p className="text-red-400 text-sm">{error}</p>}
        {notice && <p className="text-emerald-400 text-sm">{notice}</p>}
      </form>
    </div>
  );
}

// -------------------------------------------------------- security questions

function SecurityQuestionsSection() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [currentQuestions, setCurrentQuestions] = useState<(string | null)[]>([null, null, null]);
  const [editing, setEditing] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [questions, setQuestions] = useState(['', '', '']);
  const [answers, setAnswers] = useState(['', '', '']);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const status = await authApi.fetchSecurityQuestionsStatus();
      setConfigured(status.configured);
      setCurrentQuestions([status.question1, status.question2, status.question3]);
    } catch {
      setConfigured(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setNotice('');

    if (questions.some(q => !q.trim()) || answers.some(a => !a.trim())) {
      setError('Please fill in all three questions and answers.');
      return;
    }

    setBusy(true);
    try {
      await authApi.setSecurityQuestions({
        currentPassword,
        question1: questions[0], answer1: answers[0],
        question2: questions[1], answer2: answers[1],
        question3: questions[2], answer3: answers[2],
      });
      setNotice('Security questions saved.');
      setEditing(false);
      setCurrentPassword('');
      setQuestions(['', '', '']);
      setAnswers(['', '', '']);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save your security questions.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
      <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-blue-300" />
        Security questions
      </h2>
      <p className="text-sm text-blue-300/80 mb-4">
        Used to reset your password yourself if you forget it, without needing anyone
        else's help.
      </p>

      {!editing && configured !== null && (
        <div className="space-y-3">
          {configured ? (
            <ul className="text-sm text-blue-200 space-y-1 list-disc list-inside">
              {currentQuestions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          ) : (
            <p className="text-sm text-amber-300/90">Not set up yet.</p>
          )}
          <button
            onClick={() => setEditing(true)}
            className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            {configured ? 'Change questions' : 'Set up security questions'}
          </button>
          {notice && <p className="text-emerald-400 text-sm">{notice}</p>}
        </div>
      )}

      {editing && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
                  Question {i + 1}
                </label>
                <input
                  type="text"
                  value={questions[i]}
                  onChange={e => {
                    const next = [...questions];
                    next[i] = e.target.value;
                    setQuestions(next);
                    setError('');
                  }}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
                  Answer {i + 1}
                </label>
                <input
                  type="text"
                  value={answers[i]}
                  onChange={e => {
                    const next = [...answers];
                    next[i] = e.target.value;
                    setAnswers(next);
                    setError('');
                  }}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                />
              </div>
            </div>
          ))}

          <div>
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              Current password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setError(''); }}
              placeholder="Required to save"
              className="w-full sm:w-1/2 bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !currentPassword}
              className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setError(''); }}
              className="text-blue-300/70 hover:text-white text-sm px-2 py-2"
            >
              Cancel
            </button>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- passkeys

function PasskeysSection() {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPasskeys(await passkeyApi.listPasskeys());
      setError('');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your passkeys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (supportsPasskeys) load();
    else setLoading(false);
  }, [load]);

  async function handleAdd() {
    const label = window.prompt('Name this passkey (e.g. "MacBook Touch ID"):');
    if (!label?.trim()) return;

    setBusy(true);
    setError('');
    try {
      const optionsJson = await passkeyApi.passkeyRegistrationOptions();
      const options = parseCreationOptionsFromJSON(JSON.parse(optionsJson).publicKey);
      const credential = (await navigator.credentials.create({
        publicKey: options,
      })) as PublicKeyCredential;
      await passkeyApi.finishPasskeyRegistration(
        JSON.stringify(credentialToJSON(credential)),
        label.trim()
      );
      await load();
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'NotAllowedError') {
        setError('Passkey setup was cancelled.');
      } else if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('Could not register that passkey. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(passkey: Passkey) {
    if (!confirm(`Remove the passkey "${passkey.label}"?`)) return;

    setBusy(true);
    setError('');
    try {
      await passkeyApi.deletePasskey(passkey.id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not remove that passkey.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Fingerprint className="w-5 h-5 text-blue-300" />
          Passkeys
        </h2>
        {supportsPasskeys && (
          <button
            onClick={handleAdd}
            disabled={busy}
            className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            Add a passkey
          </button>
        )}
      </div>
      <p className="text-sm text-blue-300/80 mb-4">
        Sign in with Face ID, Touch ID, Windows Hello, or a security key instead of your password.
      </p>

      {!supportsPasskeys ? (
        <p className="text-sm text-amber-300/90">
          This browser doesn't support passkeys. Try a recent version of Chrome, Safari, or Edge.
        </p>
      ) : (
        <>
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

          {loading ? (
            <p className="text-blue-300">Loading…</p>
          ) : passkeys.length === 0 ? (
            <p className="text-blue-300/80 text-sm">
              No passkeys registered yet. Add one to sign in without typing your password.
            </p>
          ) : (
            <div className="space-y-3">
              {passkeys.map(passkey => (
                <div
                  key={passkey.id}
                  className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
                >
                  <div className="min-w-[200px]">
                    <p className="text-white font-medium">{passkey.label}</p>
                    <p className="text-xs text-blue-300/50 mt-0.5">
                      Added {formatDate(passkey.createdAt)}
                      {passkey.lastUsedAt != null && ` · Last used ${formatDate(passkey.lastUsedAt)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(passkey)}
                    disabled={busy}
                    title="Remove passkey"
                    className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/15 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
